import MbomPanel from '@/components/MbomPanel';
import { Drawer as MbomDrawer } from 'antd';
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import DataTable from '@/components/DataTable';
import {
  Row,
  Col,
  Input,
  Modal,
  Tooltip,
  Select,
  Popconfirm,
  Collapse,
  Card,
  Button,
} from "antd";
import { Edit2, Trash2 } from "lucide-react";
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useLocation } from "react-router-dom";
import { setEditOpDag } from '@/store/reducers/editOperationDag';
import { addField } from '@/store/reducers/headerContent';
import {
  get_recipe_details_by_recipe_id,
  delete_operation_details,
} from "../../../utils/api";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetFields } from '@/store/reducers/headerContent';
import {
  resetProcessConfig,
  setProcessConfig,
} from "../mbmr/slice/processConfig";
import ReusableSearchInput from '@/components/ReusableSearch';
import { usePageReturn } from '@/components/PageChrome';
import { defaultColors } from '@/themes/antd-theme';

function EditProcess() {
  const controllerRef = useRef();
  const dispatch = useDispatch();
  const editProcess = JSON.parse(window?.localStorage?.getItem("editProcess"));
  const [mbomOpen, setMbomOpen] = React.useState(false);
  const [recipeData, setRecipeData] = useState([]);
  const navigate = useNavigate();
  usePageReturn(() => {
    dispatch(resetFields());
    navigate(-1);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchvalue, setSearchvalue] = useState("");
  const location = useLocation();
  const {
    processId,
    processName,
    recipeName,
    recipeId,
    areaList,
    emp_roles,
    isDispens,
  } = location.state || {};

  const deleteConfirm = (record) => {
    const req = {
      operation_id: record?.operation_id,
      process_id: processId || "",
    };
    delete_operation(req);
  };

  //For collapse open at a time only one row
  const addKeysInFilterDataForCollapse = (filterData) => {
    const updatedFilterList = filterData?.map((item, index) => {
      return {
        ...item,
        key: index.toString(), // Update the key to the index converted to a string
      };
    });
    return updatedFilterList;
  };

  //############### Handle API's ##################

  const get_recipe_details = (recipe) => {
    setIsLoading(true);
    try {
      get_recipe_details_by_recipe_id(
        {
          recipe_id: parseInt(recipe),
        },
        success_get_recipe
      );
    } catch (error) {
      console.log(error);
    }
  };

  const success_get_recipe = (res) => {
    if (res?.data?.status === "success") {
      toast.success(res?.message);
      if (
        res?.data?.data[0]?.process_list &&
        res?.data?.data[0]?.process_list?.length > 0
      ) {
        const operationData = res?.data?.data[0]?.process_list?.find(
          (data) => data?.process_id === processId
        );
        setRecipeData(
          addKeysInFilterDataForCollapse(operationData?.operations)
        );
      }
    } else {
      toast.warning(res?.message);
    }
    setIsLoading(false);
  };

  const delete_operation = async (req) => {
    try {
      await delete_operation_details(req, successDeleteOperation);
    } catch (error) {
      console.log(error);
    }
  };

  const successDeleteOperation = (res) => {
    console.log(res);
    if (res?.status === "success") {
      toast.success(res?.message);
      get_recipe_details(recipeId);
    } else {
      toast.warning(res?.message);
    }
  };

  const cols = [
    {
      title: "Operation Number",
      dataIndex: "operation_number",
      key: "title",
      align: "center",
    },
    {
      title: "Operation Name",
      dataIndex: "operation_name",
      key: "title",
      align: "center",
      filteredValue: [searchvalue],
      onFilter: (value, record) => {
        return String(record.operation_name)
          .toLowerCase()
          .includes(value.toLowerCase());
      },
    },
    {
      title: "Operation Type",
      dataIndex: "operation_type",
      key: "title",
      align: "center",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "status",
      align: "center",
      width: "30%",
      render: (_, record, index) => {
        return (
          <div className="flex items-center justify-center gap-1">
            <Tooltip title="Edit Operation">
              <Button
                type="text"
                icon={<Edit2 className="w-5 h-5" style={{ color: defaultColors.warning }} />}
                onClick={() => {
                  const stateparam = {
                    operation_id: record?.operation_id,
                    process_id: processId,
                    recipe_name: recipeName,
                    process_name: processName,
                    edit_recipe_id: recipeId,
                    fromDag: "",
                  };
                  window?.localStorage?.setItem(
                    "editOperation",
                    JSON.stringify(stateparam)
                  );
                  dispatch(setEditOpDag(record?.operation_number)); // for edit dag node color highlight

                  // new flow content
                  dispatch(resetProcessConfig());
                  dispatch(
                    setProcessConfig({
                      process_id: processId,
                      operation_id: record?.operation_id,
                      process: { process: processName },
                    })
                  );
                }}
              />
            </Tooltip>

            <Popconfirm
              title="Are you sure you want to delete this Operation?"
              onConfirm={() => deleteConfirm(record)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete Operation">
                <Button
                  type="text"
                  icon={<Trash2 className="w-5 h-5" style={{ color: defaultColors.error }} />}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (recipeId || editProcess?.recipeId) {
      get_recipe_details(recipeId ? recipeId : editProcess?.recipeId);
    }

    dispatch(
      addField({
        key: "MBMR",
        value: recipeName ? recipeName : editProcess?.recipeName,
      })
    );
    dispatch(
      addField({
        key: "Process",
        value: processName ? processName : editProcess?.processName,
      })
    );
  }, [recipeId]);

  return (
    <>
      <Card
        title="Operation Data"
        extra={
          <ReusableSearchInput
            placeholder="Search here..."
            width={250}
            onSearch={(value) => setSearchvalue(value)}
            onChange={(e) => setSearchvalue(e.target.value)}
          />
        }
      >
        <Row gutter={[16, 16]}>
          {/* Back Button Row */}
          <Col span={24}>
            <div className="flex justify-end -mt-3 gap-2">
              <button type="button" onClick={() => setMbomOpen(true)} title="Master Bill of Materials — the MFR evaluated at this batch size"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border bg-transparent shadow-sm transform-gpu hover:scale-110 transition-transform duration-200"
                style={{ borderColor: defaultColors.primary, color: defaultColors.primary }}>MBOM</button>
              <MbomDrawer open={mbomOpen} onClose={() => setMbomOpen(false)} width={1000} title="Master Bill of Materials">
                <MbomPanel recipeId={recipeId} />
              </MbomDrawer>
            </div>
          </Col>

          {/* Table Row */}
          <Col span={24}>
            <DataTable
              rowKey={(record, index) => index}
              data={recipeData}
              columns={cols}
              loading={isLoading}
              pagination={{ pageSize: 10 }}
              bordered
              height={null}
            />
          </Col>
        </Row>
      </Card>
    </>
  );
}

export default EditProcess;
